import type { ReactNode } from "react";
import logoUrl from "../assets/logo.jpg";
import { fa } from "../i18n/fa";
import { Button } from "./Button";

/**
 * One-time branded welcome, shown at a brand-new user's FIRST entry (gated in `App` by
 * `SelfProfile.isNew` + a localStorage guard, so it never shows to existing users and
 * never twice). RTL, theme-aware (design tokens), subtle paired entrance — the shared
 * `bg-fadein` backdrop + `bg-pop` card, matching `ConfirmDialog`/`WinOverlay`.
 */
export function WelcomeOverlay({ onClose }: { onClose: () => void }): ReactNode {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={fa.welcome.title}
      dir="rtl"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 75,
        display: "grid",
        placeItems: "center",
        padding: 20,
        background: "rgba(0,0,0,.62)",
        animation: "bg-fadein 0.25s ease-out",
      }}
    >
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          borderRadius: "var(--radius-lg)",
          padding: "28px 22px 22px",
          width: "100%",
          maxWidth: 340,
          textAlign: "center",
          boxShadow: "0 30px 70px rgba(0,0,0,.5)",
          animation: "bg-pop .35s ease",
        }}
      >
        {/* Brand mark — logo in a soft gold-tinted ring. */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
          <div
            style={{
              padding: 3,
              borderRadius: 22,
              background:
                "linear-gradient(140deg, rgb(var(--c-secondary)), rgb(var(--c-gold-deep)))",
              boxShadow: "0 10px 26px rgba(0,0,0,.38)",
            }}
          >
            <img
              src={logoUrl}
              alt=""
              width={72}
              height={72}
              style={{ display: "block", borderRadius: 19, objectFit: "cover" }}
            />
          </div>
        </div>
        <h2 style={{ margin: "0 0 8px", fontSize: 22, fontWeight: 800, color: "var(--text)" }}>
          {fa.welcome.title}
        </h2>
        <p
          style={{
            margin: "0 0 22px",
            fontSize: 14,
            lineHeight: 1.75,
            color: "var(--hint)",
            // The body is two paragraphs separated by a blank line — without this they run
            // together into one block.
            whiteSpace: "pre-line",
          }}
        >
          {fa.welcome.body}
        </p>
        <Button variant="primary" onClick={onClose}>
          {fa.welcome.cta}
        </Button>
      </div>
    </div>
  );
}
